import {
  faCheck,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import AddTodoDrawer from "./AddTodoDrawer";
import EditTodoDrawer from "./EditTodoDrawer";
import { Skeleton } from "antd";
import { BASE_CRM_URL } from "../../../components/config/config";

const CRMToDo = () => {
  const [pendingTodos, setPendingTodos] = useState([]);
  const [finishedTodos, setFinishedTodos] = useState([]);

  const [isPendingLoading, setIsPendingLoading] = useState(false);
  const [isFinishedLoading, setIsFinishedLoading] = useState(false);

  const [pendingError, setPendingError] = useState("");
  const [finishedError, setFinishedError] = useState("");

  const [isAddTodoDrawerOpen, setIsAddTodoDrawerOpen] = useState(false);

  const [updatingTodoId, setUpdatingTodoId] = useState(null);

  const [isEditTodoDrawerOpen, setIsEditTodoDrawerOpen] = useState(false);

  const [selectedTodo, setSelectedTodo] = useState(null);
  const handleEditTodo = (todo) => {
    setSelectedTodo(todo);
    setIsEditTodoDrawerOpen(true);
  };

  const handleCloseEditTodo = () => {
    setIsEditTodoDrawerOpen(false);
    setSelectedTodo(null);
  };

  const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || "{}");

  const staffId = userDetails?.staffid;

  const formatTodoDate = (dateValue) => {
    if (!dateValue) return "N/A";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return "N/A";
    }

    const now = new Date();

    const differenceInSeconds = Math.floor(
      (now.getTime() - date.getTime()) / 1000,
    );

    // Future date
    if (differenceInSeconds < 0) {
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }

    // Less than 1 minute
    if (differenceInSeconds < 60) {
      return "Just now";
    }

    // Less than 1 hour
    const differenceInMinutes = Math.floor(differenceInSeconds / 60);

    if (differenceInMinutes < 60) {
      return `${differenceInMinutes} ${
        differenceInMinutes === 1 ? "minute" : "minutes"
      } ago`;
    }

    // Less than 24 hours
    const differenceInHours = Math.floor(differenceInMinutes / 60);

    if (differenceInHours < 24) {
      return `${differenceInHours} ${
        differenceInHours === 1 ? "hour" : "hours"
      } ago`;
    }

    // Yesterday
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear()
    ) {
      return `Yesterday, ${date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}`;
    }

    // Older than yesterday
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };
  const fetchPendingTodos = async () => {
    try {
      setIsPendingLoading(true);
      setPendingError("");

      const response = await fetch(
        `${BASE_CRM_URL}todo/GetTodos?staffid=${staffId}&finished=0`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch pending todos");
      }

      const data = await response.json();

      if (data?.Status) {
        setPendingTodos((data?.ResultData || []).slice(0, 4));
      } else {
        setPendingTodos([]);
      }
    } catch (error) {
      console.error("Pending todos error:", error);
      setPendingError("Unable to load pending tasks.");
      setPendingTodos([]);
    } finally {
      setIsPendingLoading(false);
    }
  };

  const fetchFinishedTodos = async () => {
    try {
      setIsFinishedLoading(true);
      setFinishedError("");

      const response = await fetch(
        `${BASE_CRM_URL}todo/GetTodos?staffid=${staffId}&finished=1`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch finished todos");
      }

      const data = await response.json();

      if (data?.Status) {
        setFinishedTodos((data?.ResultData || []).slice(0, 4));
      } else {
        setFinishedTodos([]);
      }
    } catch (error) {
      console.error("Finished todos error:", error);
      setFinishedError("Unable to load finished tasks.");
      setFinishedTodos([]);
    } finally {
      setIsFinishedLoading(false);
    }
  };

  const handleDeleteTodo = async (todo) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Todo?",
      text: "Are you sure you want to delete this todo?",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setUpdatingTodoId(todo.todoid);

      const response = await fetch(`${BASE_CRM_URL}todo/DeleteTodo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          todoid: todo.todoid,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to delete todo");
      }

      const data = await response.json();

      if (!data?.Status) {
        throw new Error(data?.Message || "Unable to delete todo");
      }

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: data?.Message || "Todo deleted successfully",
        timer: 1500,
        showConfirmButton: false,
      });

      // Refresh pending and finished todos
      await fetchTodos();
    } catch (error) {
      console.error("Delete Todo Error:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "Unable to delete todo",
      });
    } finally {
      setUpdatingTodoId(null);
    }
  };

  const fetchTodos = async () => {
    await Promise.all([fetchPendingTodos(), fetchFinishedTodos()]);
  };

  useEffect(() => {
    fetchTodos();
  }, [staffId]);

  /*
   * Update Todo Status
   */
  const handleTodoStatusChange = async (todo) => {
    const newFinishedStatus = todo.finished === 1 ? 0 : 1;

    try {
      setUpdatingTodoId(todo.todoid);

      const response = await fetch(`${BASE_CRM_URL}todo/UpdateTodoStatus`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          todoid: todo.todoid,
          finished: newFinishedStatus,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update todo status");
      }

      const data = await response.json();

      if (!data?.Status) {
        throw new Error(data?.Message || "Unable to update todo status");
      }

      // Refresh both lists
      await fetchTodos();
    } catch (error) {
      console.error("Update Todo Status Error:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "Unable to update todo status",
      });
    } finally {
      setUpdatingTodoId(null);
    }
  };

  const TodoSkeleton = () => {
    return (
      <>
        {[1, 2, 3, 4].map((item) => (
          <React.Fragment key={item}>
            <div className="d-flex align-items-center gap-2 my-3">
              {/* Checkbox skeleton */}
              <Skeleton.Input
                active
                size="small"
                style={{
                  width: 18,
                  height: 18,
                }}
              />

              {/* Todo content skeleton */}
              <div className="flex-fill">
                <Skeleton
                  active
                  title={{
                    width: "85%",
                  }}
                  paragraph={{
                    rows: 1,
                    width: "100px",
                  }}
                />
              </div>
            </div>

            {item !== 4 && <hr />}
          </React.Fragment>
        ))}
      </>
    );
  };
  const TodoList = ({
    todos,
    isLoading,
    errorMessage,
    completed = false,
    onEditTodo,
    onDeleteTodo,
  }) => {
    if (isLoading) {
      return <TodoSkeleton />;
    }

    if (errorMessage) {
      return <div className="text-center text-danger py-3">{errorMessage}</div>;
    }

    if (!todos.length) {
      return (
        <div className="text-center text-muted py-4">
          <i className="far fa-clipboard me-2" />
          No {completed ? "finished" : "pending"} tasks found.
        </div>
      );
    }

    return (
      <>
        {todos.map((todo, index) => (
          <React.Fragment key={todo.todoid}>
            <div className="d-flex align-items-start gap-2 my-2">
              <div className="">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={todo.finished === 1}
                  disabled={updatingTodoId === todo.todoid}
                  onChange={() => handleTodoStatusChange(todo)}
                />
              </div>

              <div className="flex-fill">
                <div className="d-flex justify-content-between">
                  <p
                    className={`text-sm lh-140 mb-1 ${
                      todo.finished === 1
                        ? "text-muted text-decoration-line-through"
                        : ""
                    }`}
                  >
                    <Link className="h6 text-decoration-none">
                      {todo.description}
                    </Link>
                  </p>
                  <div className="d-flex">
                    <button
                      type="button"
                      className="btn btn-sm btn-icon btn-light  me-1"
                      title="Edit Todo"
                      onClick={() => onEditTodo(todo)}
                      disabled={updatingTodoId === todo.todoid}
                    >
                      <i className="ti ti-edit text-secondary" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-icon btn-light "
                      title="Delete Todo"
                      onClick={() => onDeleteTodo(todo)}
                      disabled={updatingTodoId === todo.todoid}
                    >
                      <i className="ti ti-trash text-danger" />
                    </button>
                  </div>
                </div>
                <small className="text-muted">
                  <i className="far fa-clock me-1" />

                  {completed
                    ? formatTodoDate(todo.datefinished)
                    : formatTodoDate(todo.dateadded)}
                </small>
              </div>
            </div>

            {index !== todos.length - 1 && <hr />}
          </React.Fragment>
        ))}
      </>
    );
  };

  TodoList.propTypes = {
    todos: PropTypes.arrayOf(
      PropTypes.shape({
        todoid: PropTypes.number,
        description: PropTypes.string,
        staffid: PropTypes.number,
        dateadded: PropTypes.string,
        finished: PropTypes.number,
        datefinished: PropTypes.string,
        item_order: PropTypes.number,
        staff_name: PropTypes.string,
      }),
    ).isRequired,

    isLoading: PropTypes.bool.isRequired,

    errorMessage: PropTypes.string.isRequired,

    completed: PropTypes.bool,

    onEditTodo: PropTypes.func.isRequired,

    onDeleteTodo: PropTypes.func.isRequired,
  };
  return (
    <div className="card flex-fill">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div className="d-inline-flex align-items-center">
          <span className="title-icon bg-soft-indigo fs-16 me-2">
            <i className="ti ti-checks" />
          </span>

          <h5 className="card-title mb-0">To-Do</h5>
        </div>

        <button
          type="button"
          className="btn btn-primary btn-sm btn-icon"
          onClick={() => setIsAddTodoDrawerOpen(true)}
        >
          <i className="ti ti-plus" />
        </button>
      </div>

      <div className="card-body">
        <ul className="nav nav-tabs nav-justified transaction-tab mb-2">
          <li className="nav-item">
            <Link className="nav-link active" to="#latest" data-bs-toggle="tab">
              <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
              Pending
              {!isPendingLoading && (
                <span className="badge bg-warning ms-2">
                  {pendingTodos.length}
                </span>
              )}
            </Link>
          </li>

          <li className="nav-item">
            <Link className="nav-link" to="#finished" data-bs-toggle="tab">
              <FontAwesomeIcon icon={faCheck} className="me-2" />
              Finished
              {!isFinishedLoading && (
                <span className="badge bg-success ms-2">
                  {finishedTodos.length}
                </span>
              )}
            </Link>
          </li>
        </ul>

        <div className="tab-content">
          {/* Pending */}
          <div className="tab-pane show active" id="latest">
            <TodoList
              todos={pendingTodos}
              isLoading={isPendingLoading}
              errorMessage={pendingError}
              onEditTodo={handleEditTodo}
              onDeleteTodo={handleDeleteTodo}
            />
          </div>

          {/* Finished */}
          <div className="tab-pane fade" id="finished">
            <TodoList
              todos={finishedTodos}
              isLoading={isFinishedLoading}
              errorMessage={finishedError}
              completed
              onEditTodo={handleEditTodo}
              onDeleteTodo={handleDeleteTodo}
            />
          </div>
        </div>

        <AddTodoDrawer
          open={isAddTodoDrawerOpen}
          onClose={() => setIsAddTodoDrawerOpen(false)}
          onTodoAdded={fetchTodos}
        />
        <EditTodoDrawer
          open={isEditTodoDrawerOpen}
          todo={selectedTodo}
          onClose={handleCloseEditTodo}
          onTodoUpdated={fetchTodos}
        />
      </div>
    </div>
  );
};

export default CRMToDo;
